
// src/utils/imageTools.js
export async function fileToDataURL(fileOrBlob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(fileOrBlob);
    });
}

export async function dataURLToImage(dataURL) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = dataURL;
    });
}

/**
 * Render an Image to a canvas applying rotation, zoom, and cropping edges (%).
 * Returns a Blob that is compressed by target quality and maxBytes (iterative).
 */
export async function renderAndCompress({
    image,            // HTMLImageElement (already loaded)
    rotationDeg = 0,  // -180..180
    zoom = 1,         // 1 = original, >1 zoom-in
    crop = { top: 0, right: 0, bottom: 0, left: 0 }, // each 0..40 (% of final frame removed)
    maxWidth = 1600,  // final max size (longest side)
    maxHeight = 1600,
    mimeType = "image/jpeg",
    initialQuality = 0.85,
    minQuality = 0.6,
    maxBytes = 500 * 1024, // ~500 KB target
}) {
    // 1) Compute base canvas size from original with rotation
    const radians = (rotationDeg * Math.PI) / 180;
    const sin = Math.abs(Math.sin(radians));
    const cos = Math.abs(Math.cos(radians));
    const w = image.width;
    const h = image.height;

    // size needed for rotated image (bounding box)
    const bbWidth = Math.floor(w * cos + h * sin);
    const bbHeight = Math.floor(w * sin + h * cos);

    // 2) Apply zoom: we scale content, not canvas
    const targetW0 = bbWidth;
    const targetH0 = bbHeight;

    // 3) Scale down to fit within maxWidth/Height (post-crop)
    // We'll draw to a large canvas first, then crop via percentages, then resample if needed.
    let canvas = document.createElement("canvas");
    canvas.width = targetW0;
    canvas.height = targetH0;
    let ctx = canvas.getContext("2d");
    ctx.save();

    // center and rotate
    ctx.translate(targetW0 / 2, targetH0 / 2);
    ctx.rotate(radians);
    // apply zoom by scaling the drawn image
    ctx.scale(zoom, zoom);
    ctx.drawImage(image, -w / 2, -h / 2);
    ctx.restore();

    // 4) Apply crop (percent edges removed)
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    const cTop = clamp(crop.top || 0, 0, 40) / 100;
    const cRight = clamp(crop.right || 0, 0, 40) / 100;
    const cBottom = clamp(crop.bottom || 0, 0, 40) / 100;
    const cLeft = clamp(crop.left || 0, 0, 40) / 100;

    const cropX = Math.floor(canvas.width * cLeft);
    const cropY = Math.floor(canvas.height * cTop);
    const cropW = Math.floor(canvas.width * (1 - cLeft - cRight));
    const cropH = Math.floor(canvas.height * (1 - cTop - cBottom));

    // Draw cropped area onto a second canvas
    let cropped = document.createElement("canvas");
    cropped.width = cropW;
    cropped.height = cropH;
    let cctx = cropped.getContext("2d");
    cctx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    // 5) Final resize (fit within maxWidth/Height)
    const ratio = Math.min(maxWidth / cropW, maxHeight / cropH, 1);
    const finalW = Math.max(1, Math.floor(cropW * ratio));
    const finalH = Math.max(1, Math.floor(cropH * ratio));

    let finalCanvas = document.createElement("canvas");
    finalCanvas.width = finalW;
    finalCanvas.height = finalH;
    let fctx = finalCanvas.getContext("2d");
    fctx.imageSmoothingEnabled = true;
    fctx.imageSmoothingQuality = "high";
    fctx.drawImage(cropped, 0, 0, finalW, finalH);

    // 6) Iterative compression to satisfy maxBytes (if provided)
    let quality = initialQuality;
    let blob = await new Promise((resolve) =>
        finalCanvas.toBlob(resolve, mimeType, quality)
    );
    if (!blob) throw new Error("Compression failed.");

    while (blob.size > maxBytes && quality > minQuality) {
        quality = Math.max(minQuality, quality - 0.05);
        blob = await new Promise((resolve) =>
            finalCanvas.toBlob(resolve, mimeType, quality)
        );
        if (!blob) throw new Error("Compression failed.");
        if (quality === minQuality) break;
    }

    return blob;
}

/**
 * Convenience method:
 *  - read file -> image
 *  - renderAndCompress with options
 */
export async function compressFileWithOptions(fileOrBlob, options = {}) {
    const dataURL = await fileToDataURL(fileOrBlob);
    const img = await dataURLToImage(dataURL);
    return renderAndCompress({ image: img, ...options });
}
